import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { Icon } from '../Icon';
import { useGlobalSearch } from '../../../hooks/useGlobalSearch';
import { cn } from '../../lib/cn';
import { useViewport } from '../../hooks/useViewport';
import { Breadcrumbs } from './Breadcrumbs';
import { CartButton } from './CartButton';
import { MobileTopBar } from './MobileTopBar';
import { MARKET_NAV, MARKET_TABS, isMobileSearchSurface } from './navModel';
import { SkipLink, MAIN_ID } from './SkipLink';
import { TabBar } from './TabBar';
import { TopNav } from './TopNav';
import './shells.css';
import '../../patterns/shell-trail.css';

/**
 * MarketShell, the BUYING shell.
 *
 * Marketplace, listing detail, store, sets, cart, orders, wishlist, checkout.
 * Content is the hero here, so there is no sidebar on either track.
 *
 * WHAT BRANCHES ON `useViewport()` AND WHY
 * ----------------------------------------
 * The whole chrome does, because the two tracks do not render the same thing
 * with different padding, they render different things:
 *
 *   desktop  sticky 64px `.dsk-topnav` + `.dsk-breadcrumbs` + `.dsk-page`
 *   mobile   `.ds-topbar` + optional `.ds-backbtn` + floating `.ds-tabbar`
 *
 * That is two rows of the mobile-to-desktop map at once (floating tab bar to
 * top nav, floating back button to breadcrumbs). Rendering both and hiding one
 * would double the DOM, put a whole second nav in the tab order, and ship two
 * sets of handlers for one job. So exactly one tree mounts.
 *
 * WHAT DOES NOT BRANCH
 * --------------------
 * Everything inside `children`. Buttons, inputs, pills, cards and the type
 * scale differ only in presentation between the tracks, and those belong to a
 * CSS media query, not to this hook.
 *
 * @param {{
 *   children: React.ReactNode,
 *   variant?: 'default'|'checkout',
 *   breadcrumbs?: Array<{ label: string, to?: string }>,
 *   title?: string,
 *   back?: { to?: string, onClick?: () => void, label?: string },
 *   search?: { value?: string, onChange?: Function, onSubmit?: Function, placeholder?: string },
 *   showSearch?: boolean,
 *   wishlistCount?: number,
 *   onCartClick?: () => void,
 *   activeNav?: string,
 *   showTabBar?: boolean,
 *   className?: string,
 *   contentClassName?: string
 * }} props
 */
/**
 * The desktop back control.
 *
 * Mobile has had `.ds-backbtn` — the floating circular button — since the
 * conversion, and desktop was given breadcrumbs INSTEAD of a back control on
 * the theory that a trail is desktop's way back. It half is: a trail tells you
 * where the way back goes, but it makes you read three labels and hit a small
 * link to take it, and it is the only affordance on the page that is a
 * navigation control disguised as text. This is the plain version of the same
 * move, sitting on its own line directly above the trail it summarises.
 *
 * A `to` renders a real `<Link>`, so middle-click and open-in-new-tab work.
 * Only the `navigate(-1)` fallback needs a button.
 *
 * A `state` rides along with the `to`, for the same reason a crumb can carry
 * one: going back up a subtree must be able to hand the parent what this page
 * knew and it does not. A middle-click opens the bare URL without it, which is
 * correct -- a new tab has no history to remember.
 */
/* Exported so a page that renders its OWN trail can use the same control
   rather than a second copy of it. `/marketplace` is the case: on desktop it
   passes the shell no breadcrumbs and draws them in its filter rail instead,
   so the shell's trail block — and the back link inside it — never renders
   there. `navigate` is only read by the button branch; a `to` link needs
   nothing. */
export function BackLink({ back, navigate }) {
  const label = back.label || 'Go back';
  const content = (
    <>
      <Icon as={ArrowLeft} size="sm" />
      <span>Back</span>
    </>
  );

  if (back.to) {
    return (
      <Link to={back.to} state={back.state} className="dsa-backlink" aria-label={label}>
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      className="dsa-backlink"
      aria-label={label}
      onClick={back.onClick || (() => navigate(-1))}
    >
      {content}
    </button>
  );
}

/* THE PAGES A BACK CONTROL IS NOISE ON, and it is a short list.
   A back control is only pointless where the page is the ROOT of a section —
   Marketplace, Games, Dashboard. Those are the destinations the chrome holds
   permanently on both tracks, everything else in their section sits UNDER
   them, and "back" from a root means leaving the app. They are read off the
   nav model rather than typed out here so a fourth tab cannot appear without
   this rule knowing about it.

   `to`, NOT `match`. `match` is a section PREFIX — matching on it would treat
   /sets/category/swsh as a root because it starts with /sets, and a set page
   is exactly the kind of page that needs the way back.

   THE ACCOUNT TAB IS NOT A ROOT, which is the whole of what was wrong before.
   It is a tab, but nothing lives under /profile, its own trail names
   Marketplace as its parent, and it is reached as often from the desktop user
   menu or a deep link as from the tab bar — so unlike the three browsing roots
   it always has somewhere above it to return to. */
const SECTION_ROOTS = new Set(
  [...MARKET_NAV, ...MARKET_TABS]
    .filter((item) => item.id !== 'account')
    .map((item) => item.to)
    .filter(Boolean),
);

/** `/profile/` and `/profile` are the same destination to a reader. */
function normalisePath(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '');
}

export function MarketShell({
  children,
  variant = 'default',
  breadcrumbs = [],
  title,
  back,
  search,
  showSearch = true,
  wishlistCount = 0,
  wishlistTo,
  onCartClick,
  activeNav,
  showTabBar = true,
  className,
  contentClassName,
}) {
  const { isDesktop } = useViewport();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isCheckout = variant === 'checkout';

  /* THE BACK CONTROL IS DERIVED, not threaded through twenty pages.
     Every page already declares its breadcrumbs, which means every page has
     already told the shell what its parent is — the last crumb before the leaf
     that carries a `to`. Asking each one to also pass a `back` prop would be the same fact
     written twice, and the second copy is the one that goes stale.

     "WHERE APPLICABLE" IS NOT THE TRAIL'S LENGTH. It used to be — more than
     two crumbs — on the theory that two crumbs meant a top-level destination
     with nothing above it. That theory is wrong in both directions and the
     account pages are where it showed: /profile declares
     Marketplace > Profile, two crumbs, and got nothing, so a buyer who tapped
     into their account from a menu row had no way out but the tab bar. Going
     the other way, /marketplace declares Home > Marketplace, also two crumbs,
     and a "Back to Home" on the tab you land on is pure noise.

     So the question is not how long the trail is, it is TWO facts the page
     already states:

       does it name a parent?   any crumb before the last carrying a `to`
       is it a section root?    SECTION_ROOTS, read off the nav model

     A page that names a parent and is not a root gets the control. That gives
     /profile, /seller/:id and /seller-registration the back they were missing
     without putting one on /marketplace, /sets or /dashboard.

     The last crumb is excluded from the parent search because it is the page
     you are ON. A page that links its own leaf — and some do, while their
     title is still resolving — would otherwise derive a back control pointing
     at itself.

     An explicit `back` prop still wins, for the pages that leave somewhere
     unusual: checkout goes to the cart, not to the marketplace it lists.

     `navigate(-1)` is the fallback and never the default: history-back on a
     deep link, a fresh tab or a page opened from an email leaves the app
     entirely. A parent route always lands somewhere. */
  const here = normalisePath(pathname);
  const parentCrumb = breadcrumbs
    .slice(0, -1)
    .reverse()
    /* A PARENT THAT IS THE PAGE YOU ARE ON IS NOT A PARENT. Excluding the last
       crumb is not enough on its own, because one route can be served from two
       paths: `App.js` mounts `<Marketplace />` at BOTH `/` and `/marketplace`,
       and its mobile trail is Home > Marketplace. Land on `/` and the parent
       search finds the "Home" crumb, whose `to` is `/` — the page you are
       standing on. That shipped a floating "Back to Home" button on the app's
       front door that navigated to itself.

       Adding `/` to SECTION_ROOTS would have silenced this one case and left
       the next one: the rule has to hold for any page that names a crumb
       resolving to its own path, however it got there. */
    .find((crumb) => crumb.to && normalisePath(crumb.to) !== here);
  const atSectionRoot = SECTION_ROOTS.has(here);
  const derivedBack =
    back ||
    (parentCrumb && !atSectionRoot
      ? { to: parentCrumb.to, label: `Back to ${parentCrumb.label}` }
      : null);

  /* THE NAV SEARCH IS THE SHELL'S, not the page's. Before this, one page
     handed the shell a `search` object wired to its own grid filter and the
     other nine handed it nothing, so the same control in the same chrome did
     something on one route and was inert on the rest. `useGlobalSearch` gives
     every MarketShell page the same box: typeahead on focus, Enter to the
     results page. A page may still pass `search` to override it. */
  const globalSearch = useGlobalSearch();
  const searchProps = search || globalSearch;

  /* THE SAME BOX ON THE PHONE, and until now there was none anywhere on the
     mobile track: `searchProps` was built here and then handed only to
     `TopNav`, which does not mount below 1024. The tab bar is Shop / Games /
     Dashboard / Account, the palette is Cmd-K, and `/search` was a URL you had
     to type.

     It is not on every page, because the mobile bar has one centre slot and on
     a leaf page the title in it is the only thing naming the page. See
     `isMobileSearchSurface`. */
  const mobileSearch =
    showSearch && !isCheckout && isMobileSearchSurface(here) ? searchProps : null;

  if (isDesktop) {
    return (
      <div className={cn('dsk-body', className)}>
        <SkipLink track="desktop" />
        <TopNav
          variant={variant}
          search={searchProps}
          showSearch={showSearch && !isCheckout}
          wishlistCount={wishlistCount}
          wishlistTo={wishlistTo}
          onCartClick={onCartClick}
          activeNav={activeNav}
        />
        <main id={MAIN_ID} className={cn('dsk-page', contentClassName)}>
          {/* Back ABOVE the trail, both starting at the page's own left edge.
              Source order is the visual order — `.dsa-shell__trail` is a
              column flex — so this needs no CSS ordering and reads the same to
              a screen reader as it looks. The left edge is `.dsk-page`'s
              content box, the same one the page H1 below sits in; see
              `patterns/shell-trail.css` for why that used to be off by a
              second `--dsk-gutter`. */}
          {/* A ONE-CRUMB TRAIL IS NOT A TRAIL, and on desktop it renders as
              stray text floating above the page title — which is exactly what
              Junaid kept reporting on /dashboard as "weird floating dashboard
              text above the dashboard header". The page passes
              `breadcrumbs={[{ label: 'Dashboard' }]}` because the MOBILE top
              bar titles itself from the last crumb, and the desktop trail then
              printed "Dashboard" directly over the "Dashboard" H1.

              A trail exists to name the path ABOVE you. With a single crumb
              there is no path, only the page you can already see. So the
              desktop trail needs two or more; the mobile bar still reads the
              same array for its title and is unaffected. */}
          {breadcrumbs.length > 1 && (
            <div className="dsa-shell__trail">
              {derivedBack ? <BackLink back={derivedBack} navigate={navigate} /> : null}
              <Breadcrumbs items={breadcrumbs} />
            </div>
          )}
          {children}
        </main>
      </div>
    );
  }

  // The tab bar floats over the page, so the scaffold has to reserve room for
  // it or the last row of every page is unreachable.
  const tabbed = showTabBar && !isCheckout;

  return (
    <div className={cn('ds-appshell', tabbed && 'ds-appshell--tabbed', className)}>
      <SkipLink track="mobile" />
      {/* The right slot is the CART, not the theme toggle.
          Appearance is a preference you set once and forget; the cart is live
          state you check constantly, and it is the one control that earns
          permanent real estate in a 44px slot. It is also where the cart was
          before the conversion. The theme toggle moved into the account
          section, which is where a once-a-month preference belongs. */}
      {/* THE WORDMARK TAKES THE CENTRE ON EVERY SEARCH SURFACE, and it is the
          same reasoning that used to justify the search FIELD taking it: the
          title it displaces is one of Marketplace / Games / Search, three
          labels the tab bar underneath is already showing. The difference is
          what fills the slot. The field did, and the brand went missing from
          the three surfaces a buyer spends their time on; now search is a 44px
          trigger in the end slot and the slot goes back to the logo. */}
      <MobileTopBar
        title={title || (breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].label : '')}
        wordmark={Boolean(mobileSearch) || (!title && breadcrumbs.length === 0)}
        back={derivedBack}
        search={mobileSearch}
        end={isCheckout ? null : <CartButton />}
      />
      <main id={MAIN_ID} className={cn('ds-appshell__main', contentClassName)}>
        {children}
      </main>
      {/* No `wishlistCount` here any more: the bar reads the favourites store
          itself, for the reason spelled out in TabBar's own docblock. The prop
          stays on this shell because the DESKTOP `TopNav` heart above still
          takes it, and that one is fed by the pages that have the number to
          hand. */}
      {tabbed && <TabBar activeId={activeNav} />}
    </div>
  );
}

export default MarketShell;
